<template>
  <a-modal
    title="Associate Document Template"
    width="600px"
    :visible="visible"
    :destroy-on-close="true"
    @cancel="closeModal"
    v-if="visible"
  >
    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-2 flex-column"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-model-item :colon="false" label-align="left" label="Name" prop="name">
        <a-input :disabled="isDisabledUpdate" v-model="form.name" placeholder="My worker license" />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Details" prop="description">
        <a-textarea
          :disabled="isDisabledUpdate"
          v-model="form.description"
          placeholder="My license to work on both sides"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Type" prop="type">
        <custom-select
          :disabled="isDisabledUpdate"
          :multiple="false"
          :value.sync="form.type"
          placeholder="Type"
          :options="types"
          name="name"
          optionKey="id"
        />
      </a-form-model-item>

      <a-form-model-item
        :colon="false"
        label-align="left"
        label="Required in Company"
        prop="required_in_project"
        extra="Every Associate must have and keep this Document up-t-date in order to work at the Company"
      >
        <a-switch :disabled="isDisabledUpdate" v-model="form.required_in_project" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Responsible Party" prop="attachment_responsible">
        <a-select
          :disabled="isDisabledUpdate"
          v-model="form.attachment_responsible"
          placeholder="Responsible Party"
          @change="handleAttachmentResponsibleChange"
        >
          <a-select-option v-for="option in attachmentResponsibleOptions" :key="option.id" :value="option.id">
            {{ option.name }}
          </a-select-option>
        </a-select>
      </a-form-model-item>

      <a-form-model-item
        :colon="false"
        label-align="left"
        label="Critical for Work"
        prop="critical"
        extra="When an Associate’s document expires, their work in the company will be put ON HOLD until they renew it."
      >
        <a-switch :disabled="isDisabledUpdate" v-model="form.critical" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Has Expiration Date" prop="can_expire">
        <a-switch v-model="form.can_expire" :disabled="isDisabledUpdate" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Has Document Number" prop="has_number">
        <a-switch v-model="form.has_number" :disabled="isDisabledUpdate" />
      </a-form-model-item>

      <a-form-model-item
        v-if="!isDisabledUpdate || form.example_files.length"
        prop="files"
        :colon="false"
        label-align="left"
        label="Example Files"
      >
        <document-attachments
          :with-title="false"
          :files="form.example_files"
          @save="saveAttachmentsIds"
          :is-view="isDisabledUpdate"
          accept=".png,.jpg,.jpeg,.svg"
        />
      </a-form-model-item>

      <h4 class="ant-form-item-no-colon m-0">Target</h4>

      <div class="flex gap-2 flex-column">
        <a-form-model-item :colon="false" label-align="left" label="Payment Types" prop="scope.payment_types">
          <custom-select
            :disabled="isDisabledUpdate"
            :multiple="true"
            :value.sync="paymentTypeProxy"
            placeholder="Payment Types"
            :options="paymentTypes"
            name="name"
            optionKey="id"
          />
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Positions" prop="scope.roles">
          <custom-select
            :disabled="isDisabledUpdate"
            :multiple="true"
            :value.sync="roleProxy"
            placeholder="Position"
            :options="roles"
            name="name"
            :optionKey="isDisabledUpdate ? 'parent_entity' : 'id'"
          />
        </a-form-model-item>
        <a-form-model-item
          label=" "
          :colon="false"
          label-align="left"
          help="By default, you select the list of entities that will be
              targeted. By using this option, you can target all entities
              except the ones selected in the box."
        >
          <a-checkbox :checked="excludeRolesMode" @change="handleExcludeRolesModeChange">
            Target All Except Selected
          </a-checkbox>
        </a-form-model-item>

        <a-form-model-item :colon="false" label-align="left" label="Legal Entities" prop="scope.legal_entities">
          <custom-select
            :disabled="isDisabledUpdate"
            :multiple="true"
            :value.sync="agencyProxy"
            placeholder="Legal Entities"
            :options="legalEntities"
            name="name"
            :optionKey="isDisabledUpdate ? 'parent_entity' : 'id'"
          />
        </a-form-model-item>
        <a-form-model-item
          label=" "
          :colon="false"
          label-align="left"
          help="By default, you select the list of entities that will be
              targeted. By using this option, you can target all entities
              except the ones selected in the box."
        >
          <a-checkbox :checked="excludeLegalEntitiesMode" @change="handleExcludeLegalEntitiesModeChange">
            Target All Except Selected
          </a-checkbox>
        </a-form-model-item>

        <a-form-model-item
          v-if="hasContractorPaymentType"
          :colon="false"
          label-align="left"
          label="Contractor Types"
          prop="scope.contractor_sub_types"
        >
          <custom-select
            :disabled="isDisabledUpdate"
            :multiple="true"
            :value.sync="contractorSubTypeProxy"
            placeholder="Contractor Types"
            :options="contractorSubTypes"
            name="name"
            optionKey="id"
          />
        </a-form-model-item>
      </div>
    </a-form-model>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal"> Cancel </a-button>
        <a-button v-if="!isDisabledUpdate" icon="save" type="primary" @click="save" :loading="loading"> Save </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import CustomSelect from '@/components/ui/custom-select.vue'
import { Button, Checkbox, FormModel, Modal, Input, notification, Select, Switch } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { CONTRACTOR_SUB_TYPES } from '@/store/modules/legal-entity/constants'
import { deepCopy } from '@/utils/deep-copy'
import { normalizePaymentTypes as normalizePaymentTypeValues } from '@/utils/payment-types'
import { normalizeTargetScopeIds } from '@/utils/target-scope'

const { mapActions: documentActions } = createNamespacedHelpers('document')
const { mapActions: refBookActions } = createNamespacedHelpers('refbook')

const ATTACHMENT_RESPONSIBLE_OPTIONS = Object.freeze([
  { id: 'worker', name: 'Worker' },
  { id: 'manager', name: 'Manager' }
])

const initialForm = {
  name: undefined,
  description: undefined,
  type: undefined,
  required_in_project: false,
  attachment_responsible: 'worker',
  required_document_responsible: 'worker',
  critical: true,
  has_number: true,
  can_expire: true,
  scope: {
    legal_entities: [],
    exclude_legal_entities: [],
    contractor_sub_types: [],
    exclude_contractor_sub_types: [],
    roles: [],
    exclude_roles: [],
    payment_types: [],
    exclude_payment_types: []
  },
  example_files: []
}

const DELAYED_DOCUMENT_FLAGS_MESSAGE = "Changes saved. They'll take effect within 15 minutes."
const DELAYED_DOCUMENT_FLAGS = ['critical', 'required_in_project']

const initialRules = {
  name: [
    {
      required: true,
      message: 'Name can not be empty',
      trigger: 'change'
    },
    {
      pattern: /^[^/\\]*$/,
      message: 'Symbols / and \\ are not allowed',
      trigger: 'change'
    }
  ],
  type: [
    {
      required: true,
      message: 'Type can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-textarea': Input.TextArea,
    'a-modal': Modal,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input': Input,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-switch': Switch,
    'custom-select': CustomSelect,
    'document-attachments': ShiftFormAttachments
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    template: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,

      form: { ...initialForm },
      rules: {
        ...initialRules
      },

      types: [],
      roles: this.$store.state.applicationState.projectRoles,
      attachmentResponsibleOptions: ATTACHMENT_RESPONSIBLE_OPTIONS,
      paymentTypes: this.$store.state.applicationState.paymentTypes.map(item => ({
        name: item.name,
        id: item.name.toLowerCase()
      })),
      legalEntities: this.$store.state.applicationState.projectLegalEntities,
      contractorSubTypes: CONTRACTOR_SUB_TYPES,
      excludeRolesMode: false,
      excludeLegalEntitiesMode: false,
      isScopeHydrating: false,
      documentFlagsAlertShown: false
    }
  },
  watch: {
    template: {
      handler(newVal) {
        this.isScopeHydrating = true
        if (newVal) {
          const form = deepCopy({
            ...newVal,
            attachment_responsible:
              newVal.required_document_responsible ||
              newVal.attachment_responsible ||
              initialForm.attachment_responsible,
            required_document_responsible:
              newVal.required_document_responsible ||
              newVal.attachment_responsible ||
              initialForm.required_document_responsible
          })
          const scope = form.scope ?? {}
          const paymentTypes = this.normalizePaymentTypes(scope.payment_types)
          const hasContractor = paymentTypes.includes('contractor')

          form.type = newVal.type.id
          form.scope = {
            ...initialForm.scope,
            ...scope,
            payment_types: paymentTypes,
            legal_entities: normalizeTargetScopeIds(scope.legal_entities),
            exclude_legal_entities: normalizeTargetScopeIds(scope.exclude_legal_entities),
            contractor_sub_types: hasContractor ? normalizeTargetScopeIds(scope.contractor_sub_types) : []
          }

          this.excludeRolesMode = !!form.scope.exclude_roles.length && !form.scope.roles.length
          this.excludeLegalEntitiesMode =
            !!form.scope.exclude_legal_entities.length && !form.scope.legal_entities.length
          this.form = form
          this.attachmentsIds = newVal.example_files.map(item => ({
            id: item.id
          }))
        } else {
          this.form = deepCopy(initialForm)
          this.excludeRolesMode = false
          this.excludeLegalEntitiesMode = false
          this.attachmentsIds = []
        }
        this.$nextTick(() => {
          this.isScopeHydrating = false
        })
      },
      immediate: true,
      deep: true
    },
    visible: {
      handler(newVal) {
        if (newVal) {
          this.documentFlagsAlertShown = false
        }

        if (!newVal) {
          this.isScopeHydrating = true
          this.form = deepCopy(initialForm)
          this.excludeRolesMode = false
          this.excludeLegalEntitiesMode = false
          this.attachmentsIds = []
          this.documentFlagsAlertShown = false
          this.isScopeHydrating = false
        }
      }
    },
    'form.scope.payment_types': {
      handler(newVal, oldVal) {
        if (this.isScopeHydrating) return

        const hasContractor = this.normalizePaymentTypes(newVal).includes('contractor')
        const hadContractor = this.normalizePaymentTypes(oldVal).includes('contractor')

        if (!hasContractor) {
          this.form.scope.contractor_sub_types = []
          return
        }

        if (!hadContractor) {
          this.form.scope.contractor_sub_types = this.contractorSubTypes.map(item => item.id)
        }
      },
      deep: true
    }
  },
  computed: {
    isProjectHost() {
      return this.$store.state.applicationState.currentProject.sequences_host
    },

    isDisabledUpdate() {
      return !this.isProjectHost && this.form?.is_public
    },
    hasContractorPaymentType() {
      return this.paymentTypeProxy.includes('contractor')
    },
    paymentTypeProxy: {
      get() {
        return this.normalizePaymentTypes(this.form.scope.payment_types)
      },
      set(value) {
        this.form.scope.payment_types = this.normalizePaymentTypes(value)
      }
    },

    roleProxy: {
      get() {
        const roles = this.excludeRolesMode ? this.form.scope.exclude_roles : this.form.scope.roles
        return roles ? roles.map(item => item.id) : undefined
      },
      set(value) {
        const mappedValue = value.map(item => ({ id: item }))
        if (this.excludeRolesMode) {
          this.form.scope.exclude_roles = mappedValue
          this.form.scope.roles = []
          return
        }
        this.form.scope.roles = mappedValue
        this.form.scope.exclude_roles = []
      }
    },

    agencyProxy: {
      get() {
        const legalEntities = this.excludeLegalEntitiesMode
          ? this.form.scope.exclude_legal_entities
          : this.form.scope.legal_entities
        return legalEntities || undefined
      },
      set(value) {
        if (this.excludeLegalEntitiesMode) {
          this.form.scope.exclude_legal_entities = value
          this.form.scope.legal_entities = []
          return
        }
        this.form.scope.legal_entities = value
        this.form.scope.exclude_legal_entities = []
      }
    },
    contractorSubTypeProxy: {
      get() {
        return this.form.scope.contractor_sub_types?.length ? this.form.scope.contractor_sub_types : undefined
      },
      set(value) {
        this.form.scope.contractor_sub_types = value
      }
    }
  },
  methods: {
    ...documentActions(['patchAssociateTemplate', 'postAssociateTemplate']),
    ...refBookActions(['getRefBook']),

    closeModal() {
      this.excludeRolesMode = false
      this.excludeLegalEntitiesMode = false
      this.$emit('update:visible', false)
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },
    hasDelayedDocumentFlagsChanges() {
      const initial = this.form?.initial

      if (!initial) return false

      return DELAYED_DOCUMENT_FLAGS.some(field => Boolean(this.form?.[field]) !== Boolean(initial?.[field]))
    },
    showDelayedDocumentFlagsAlert() {
      if (this.documentFlagsAlertShown || !this.hasDelayedDocumentFlagsChanges()) return

      this.documentFlagsAlertShown = true
      this.showNotification('info', DELAYED_DOCUMENT_FLAGS_MESSAGE)
    },
    handleExcludeRolesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeRolesMode) return

      this.excludeRolesMode = checked
      if (checked) {
        this.form.scope.exclude_roles = [...this.form.scope.roles]
        this.form.scope.roles = []
        return
      }
      this.form.scope.roles = [...this.form.scope.exclude_roles]
      this.form.scope.exclude_roles = []
    },
    handleExcludeLegalEntitiesModeChange(event) {
      if (this.isScopeHydrating) return
      const checked = !!event?.target?.checked
      if (checked === this.excludeLegalEntitiesMode) return

      this.excludeLegalEntitiesMode = checked
      if (checked) {
        this.form.scope.exclude_legal_entities = [...this.form.scope.legal_entities]
        this.form.scope.legal_entities = []
        return
      }
      this.form.scope.legal_entities = [...this.form.scope.exclude_legal_entities]
      this.form.scope.exclude_legal_entities = []
    },
    handleAttachmentResponsibleChange(value) {
      this.form.required_document_responsible = value
    },
    normalizeScope(scope) {
      return {
        ...scope,
        roles: this.excludeRolesMode ? [] : (scope.roles ?? []),
        exclude_roles: this.excludeRolesMode ? (scope.exclude_roles ?? []) : [],
        legal_entities: this.excludeLegalEntitiesMode ? [] : (scope.legal_entities ?? []),
        exclude_legal_entities: this.excludeLegalEntitiesMode ? (scope.exclude_legal_entities ?? []) : [],
        payment_types: this.normalizePaymentTypes(scope.payment_types),
        exclude_payment_types: scope.exclude_payment_types ?? [],
        contractor_sub_types: this.hasContractorPaymentType ? (scope.contractor_sub_types ?? []) : [],
        exclude_contractor_sub_types: scope.exclude_contractor_sub_types ?? []
      }
    },
    normalizePaymentTypes(paymentTypes = []) {
      return normalizePaymentTypeValues(paymentTypes, this.$store.state.applicationState.paymentTypes)
    },
    saveAttachmentsIds(ids, flag) {
      this.attachmentsIds = ids

      if (flag !== 'remove') {
        return
      }

      if (!Array.isArray(this.form.example_files)) {
        this.form.example_files = []
        return
      }

      const attachmentIds = new Set(ids.map(item => item?.id).filter(id => typeof id === 'number'))

      this.form.example_files = this.form.example_files.filter(file => attachmentIds.has(file.id))
    },

    save() {
      const tempFiles = deepCopy(this.form.example_files)
      this.form.example_files = this.attachmentsIds

      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.form.example_files = tempFiles
          return
        }
        this.loading = true

        try {
          const payload = {
            template: {
              ...this.form,
              required_document_responsible: this.form.attachment_responsible,
              scope: this.normalizeScope(this.form.scope),
              type: this.form.type,
              project: this.$store.state.applicationState.currentProject.id
            },
            template_id: undefined
          }

          payload.template_id = this.template ? this.template.id : undefined

          const data = this.template
            ? await this.patchAssociateTemplate(payload)
            : await this.postAssociateTemplate(payload)

          this.showNotification(
            'success',
            'Success',
            `Associate document template has been ${this.template ? 'updated' : 'created'} successfully.`
          )
          this.showDelayedDocumentFlagsAlert()

          this.$emit('save', data, this.template ? 'edit' : 'save')
          this.closeModal()
        } catch (error) {
          this.showNotification('error', 'Error', error?.message || error)
        } finally {
          this.loading = false
        }
      })
    },

    async fetchTypes() {
      this.loading = true

      const payload = {
        name: 'AssociateDocumentType'
      }

      try {
        const data = await this.getRefBook(payload)

        this.types = data
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  },
  async mounted() {
    if (!this.types.length) await this.fetchTypes()
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-calendar-picker {
  width: 100%;
}

.ant-checkbox-wrapper + .ant-checkbox-wrapper {
  margin: 0;
}
</style>
